import { defineStore } from 'pinia'
import api from '@/api/axios'
import { ref, computed } from 'vue'

export const useSessionStore = defineStore('session', () => {
  const session = ref(null)
  const sessions = ref([])
  const sessionMetadata = ref(null) // NEW: Store enhanced metadata from API
  const loading = ref(false)
  const error = ref(null)

  // SESSION STATUS FIELDS EXPLANATION:
  // - `status`: Workflow/process status (searching, completed, in_progress, etc.)
  // - `sessionState`: Lifecycle status (active, archived, deleted)
  // These are two distinct concepts tracked by the API

  // PHASE 1: Enhanced Session Management with Rich Metadata
  async function fetchSessions(projectId, options = {}) {
    if (!projectId) throw new Error('No projectId provided')
    loading.value = true
    error.value = null
    
    try {
      // Build query parameters based on API spec
      const params = new URLSearchParams()
      if (options.status) params.append('status', options.status)
      if (options.phase) params.append('phase', options.phase)
      if (options.bookmarked !== undefined) params.append('bookmarked', options.bookmarked)
      if (options.tags) params.append('tags', Array.isArray(options.tags) ? options.tags.join(',') : options.tags)
      if (options.sortBy) params.append('sortBy', options.sortBy)
      if (options.sortOrder) params.append('sortOrder', options.sortOrder)
      if (options.limit) params.append('limit', options.limit)
      if (options.page) params.append('page', options.page)
      
      const queryString = params.toString()
      const url = `/sessions/${projectId}/sessions${queryString ? '?' + queryString : ''}`
      
      const { data } = await api.get(url)
      sessions.value = data.sessions || []
      
      // Enhance sessions with smart primary actions if they have generic ones
      sessions.value = sessions.value.map(session => {
        // If session has a generic primaryAction, replace it with a smart one
        if (session.primaryAction?.label === 'View Selected Entity' || 
            !session.primaryAction) {
          return {
            ...session,
            primaryAction: getSmartPrimaryAction(session)
          }
        }
        return session
      })
      
      // Store enhanced metadata from API response
      sessionMetadata.value = data.metadata || null
      
      return data
    } catch (err) {
      error.value = err.response?.data?.message || 'Failed to fetch sessions'
      throw err
    } finally {
      loading.value = false
    }
  }

  async function loadSession(projectId, sessionId) {
    if (!projectId || !sessionId) throw new Error('Missing projectId or sessionId')
    loading.value = true
    error.value = null
    
    try {
      const { data } = await api.get(`/sessions/${projectId}/sessions/${sessionId}`)
      session.value = data.session
      
      if (session.value?._id) {
        localStorage.setItem('diligint-session-id', session.value._id)
        localStorage.setItem('diligint-project-id', projectId)
      }
      return data
    } catch (err) {
      error.value = err.response?.data?.message || 'Failed to load session'
      throw err
    } finally {
      loading.value = false
    }
  }

  async function createSession(projectId, payload) {
    if (!projectId) throw new Error('No projectId provided')
    loading.value = true
    error.value = null
    
    try {
      const { data } = await api.post(`/sessions/${projectId}/sessions`, payload)
      session.value = data.session
      
      if (session.value?._id) {
        localStorage.setItem('diligint-session-id', session.value._id)
        localStorage.setItem('diligint-project-id', projectId)
      }
      return session.value
    } catch (err) {
      error.value = err.response?.data?.message || 'Failed to create session'
      throw err
    } finally {
      loading.value = false
    }
  }

  async function updateSession(projectId, sessionId, payload) {
    if (!projectId || !sessionId) throw new Error('Missing projectId or sessionId')
    loading.value = true
    error.value = null
    
    console.log('updateSession called with:', { projectId, sessionId, payload })
    
    try {
      const { data } = await api.put(`/sessions/${projectId}/sessions/${sessionId}`, payload)
      
      console.log('API response:', data)
      
      // Update current session if it matches
      if (session.value && session.value._id === sessionId) {
        session.value = data.session
        console.log('Updated current session:', session.value)
      }
      
      // Update session in sessions list
      const sessionIndex = sessions.value.findIndex(s => s._id === sessionId)
      if (sessionIndex !== -1) {
        sessions.value[sessionIndex] = data.session
        console.log('Updated session in list at index', sessionIndex, ':', sessions.value[sessionIndex])
      } else {
        console.warn('Session not found in sessions list:', sessionId)
      }
      
      return data
    } catch (err) {
      error.value = err.response?.data?.message || 'Failed to update session'
      throw err
    } finally {
      loading.value = false
    }
  }

  // Update session phase (Legacy - use navigateToPhase for new implementations)
  async function updateSessionPhase(projectId, sessionId, phase, notes) {
    if (!projectId || !sessionId) throw new Error('Missing projectId or sessionId')
    loading.value = true
    error.value = null
    
    try {
      const response = await api.patch(`/sessions/${projectId}/sessions/${sessionId}/phase`, {
        phase,
        notes
      })
      
      // Update current session if it matches
      if (session.value && session.value._id === sessionId) {
        session.value = { ...session.value, ...response.data.session }
      }
      
      // Update session in sessions list
      const sessionIndex = sessions.value.findIndex(s => s._id === sessionId)
      if (sessionIndex !== -1) {
        sessions.value[sessionIndex] = { ...sessions.value[sessionIndex], ...response.data.session }
      }
      
      return response.data
    } catch (err) {
      error.value = err.response?.data?.message || 'Failed to update session phase'
      throw err
    } finally {
      loading.value = false
    }
  }

  // NEW: Get comprehensive session progress information
  async function getSessionProgress(projectId, sessionId) {
    if (!projectId || !sessionId) throw new Error('Missing projectId or sessionId')
    loading.value = true
    error.value = null
    
    try {
      const response = await api.get(`/sessions/${projectId}/sessions/${sessionId}/progress`)
      return response.data
    } catch (err) {
      // Gracefully handle when backend doesn't support progress endpoint yet
      if (err.response?.status === 404) {
        console.warn('Session progress endpoint not implemented yet')
        // Return basic progress info based on session data
        const sessionData = sessions.value.find(s => s._id === sessionId) || session.value
        if (sessionData) {
          return {
            progress: {
              currentPhase: sessionData.phase || 'disambiguation',
              progressPercent: getProgressPercentage(sessionData.phase),
              canGoBack: true,
              canGoForward: sessionData.phase !== 'completed'
            },
            availableActions: getDefaultActions(sessionData),
            dataStats: sessionData.dataCount || null
          }
        }
        return null
      }
      error.value = err.response?.data?.message || 'Failed to get session progress'
      throw err
    } finally {
      loading.value = false
    }
  }

  // NEW: Navigate to specific phase with validation
  async function navigateToPhase(projectId, sessionId, targetPhase, notes) {
    if (!projectId || !sessionId) throw new Error('Missing projectId or sessionId')
    loading.value = true
    error.value = null
    
    try {
      const response = await api.post(`/sessions/${projectId}/sessions/${sessionId}/navigate-to-phase`, {
        phase: targetPhase,
        notes
      })
      
      // Update current session if it matches
      if (session.value && session.value._id === sessionId) {
        session.value = { ...session.value, ...response.data.session }
      }
      
      // Update session in sessions list
      const sessionIndex = sessions.value.findIndex(s => s._id === sessionId)
      if (sessionIndex !== -1) {
        sessions.value[sessionIndex] = { ...sessions.value[sessionIndex], ...response.data.session }
      }
      
      return response.data
    } catch (err) {
      // Fallback to legacy phase update if new endpoint not available
      if (err.response?.status === 404) {
        console.warn('Phase navigation endpoint not available, falling back to legacy update')
        return await updateSessionPhase(projectId, sessionId, targetPhase, notes)
      }
      error.value = err.response?.data?.message || 'Failed to navigate to phase'
      throw err
    } finally {
      loading.value = false
    }
  }

  // Helper function to calculate progress percentage based on phase
  function getProgressPercentage(phase) {
    const phaseMap = {
      'disambiguation': 20,
      'data_collection': 40,
      'analysis': 60,
      'reporting': 80,
      'completed': 100
    }
    return phaseMap[phase] || 0
  }

  // Helper function to generate smart primary actions based on session state
  function getSmartPrimaryAction(session) {
    const phase = session.phase
    const hasEntity = session.entityInfo?.primary
    const dataCount = session.dataCount
    
    switch (phase) {
      case 'completed':
        return {
          label: 'View Final Report',
          icon: 'DocumentIcon',
          type: 'success'
        }
        
      case 'reporting':
        return {
          label: 'Generate Report →',
          icon: 'ChartBarIcon',
          type: 'primary'
        }
        
      case 'analysis':
        if (dataCount && dataCount.total > 0) {
          const approvedCount = dataCount.approved || 0
          const totalCount = dataCount.total
          const percentage = Math.round((approvedCount / totalCount) * 100)
          return {
            label: `Review Analysis (${approvedCount}/${totalCount} • ${percentage}%)`,
            icon: 'BeakerIcon',
            type: 'primary'
          }
        }
        return {
          label: 'View Analysis Results →',
          icon: 'BeakerIcon',
          type: 'primary'
        }
        
      case 'data_collection':
        if (dataCount && dataCount.total > 0) {
          const approvedCount = dataCount.approved || 0
          const totalCount = dataCount.total
          const percentage = Math.round((approvedCount / totalCount) * 100)
          return {
            label: `Review Data (${approvedCount}/${totalCount} • ${percentage}%)`,
            icon: 'ChartBarIcon',
            type: 'primary'
          }
        }
        return {
          label: 'Start Data Collection →',
          icon: 'PlayIcon',
          type: 'primary'
        }
        
      case 'disambiguation':
      default:
        if (hasEntity) {
          return {
            label: 'Start Data Collection →',
            icon: 'PlayIcon',
            type: 'primary'
          }
        } else {
          return {
            label: 'Complete Disambiguation →',
            icon: 'CursorArrowRaysIcon',
            type: 'primary'
          }
        }
    }
  }

  // Helper function to generate default actions when progress endpoint unavailable
  function getDefaultActions(sessionData) {
    const actions = []
    const phase = sessionData.phase
    const hasEntity = sessionData.entityInfo?.primary
    
    // Add primary entity view action based on phase
    if (hasEntity) {
      let label, icon
      
      switch (phase) {
        case 'data_collection':
        case 'analysis':
        case 'reporting':
        case 'completed':
          label = 'View Entity & Data'
          icon = '📊'
          break
        case 'disambiguation':
          label = 'View Entity Details'
          icon = '👤'
          break
        default:
          label = 'View Selected Entity'
          icon = '👤'
      }
      
      actions.push({
        label,
        icon,
        action: 'view-entity',
        type: 'info'
      })
    }
    
    // Add phase-specific primary actions
    switch (phase) {
      case 'disambiguation':
        if (!hasEntity) {
          actions.push({
            label: 'Continue Disambiguation',
            icon: '🔍',
            action: 'disambiguation',
            type: 'primary'
          })
        } else {
          actions.push({
            label: 'Start Data Collection',
            icon: '📊',
            action: 'data-collection',
            type: 'primary'
          })
        }
        break
      case 'data_collection':
        actions.push({
          label: 'Continue Data Collection',
          icon: '📊',
          action: 'data-collection',
          type: 'primary'
        })
        break
      case 'analysis':
        actions.push({
          label: 'View Analysis',
          icon: '📈',
          action: 'analysis',
          type: 'primary'
        })
        break
      case 'reporting':
        actions.push({
          label: 'Generate Report',
          icon: '📋',
          action: 'report',
          type: 'primary'
        })
        break
      case 'completed':
        actions.push({
          label: 'View Final Report',
          icon: '📋',
          action: 'final-report',
          type: 'primary'
        })
        break
    }
    
    return actions
  }

  // Toggle bookmark status
  async function toggleBookmark(projectId, sessionId, bookmarked) {
    if (!projectId || !sessionId) throw new Error('Missing projectId or sessionId')
    loading.value = true
    error.value = null
    
    try {
      const response = await api.post(`/sessions/${projectId}/sessions/${sessionId}/bookmark`, {
        bookmarked
      })
      
      // Update current session if it matches
      if (session.value && session.value._id === sessionId) {
        session.value = { ...session.value, ...response.data.session }
      }
      
      return response.data
    } catch (err) {
      error.value = err.response?.data?.message || 'Failed to toggle bookmark'
      throw err
    } finally {
      loading.value = false
    }
  }

  // Manage session tags
  async function manageTags(projectId, sessionId, action, tags) {
    if (!projectId || !sessionId) throw new Error('Missing projectId or sessionId')
    loading.value = true
    error.value = null
    
    try {
      const response = await api.post(`/sessions/${projectId}/sessions/${sessionId}/tags`, {
        action, // 'add' or 'remove'
        tags
      })
      
      // Update current session if it matches
      if (session.value && session.value._id === sessionId) {
        session.value = { ...session.value, ...response.data.session }
      }
      
      return response.data
    } catch (err) {
      error.value = err.response?.data?.message || 'Failed to manage tags'
      throw err
    } finally {
      loading.value = false
    }
  }

  // ENHANCED: Updated disambiguation with structured data support
  async function disambiguateEntity(projectId, sessionId, payload) {
    if (!projectId || !sessionId) throw new Error('Missing projectId or sessionId')
    loading.value = true
    error.value = null
    
    try {
      // Enhanced payload formatting for structured disambiguation
      const disambiguationPayload = {
        query: payload.query || payload.searchQuery,
        entityType: payload.entityType || 'auto',
        
        // NEW: Identifiers support (matches API spec)
        identifiers: payload.identifiers || payload.structuredIdentifiers || {},
        
        // NEW: Search options (matches API spec)
        searchOptions: {
          prioritizeStructuredData: payload.prioritizeStructuredData ?? true,
          exactMatchRequired: payload.exactMatchRequired ?? false,
          includeAliases: payload.includeAliases ?? true
        },
        
        // API spec options field (reserved for future use)
        options: payload.options || {}
      }
      
      console.log('Enhanced disambiguation payload:', disambiguationPayload)
      
      const { data } = await api.post(
        `/sessions/${projectId}/sessions/${sessionId}/disambiguate`, 
        disambiguationPayload
      )
      
      console.log('Enhanced disambiguation response:', data)
      
      // Update current session if it matches
      if (session.value && session.value._id === sessionId) {
        session.value = { 
          ...session.value,
          // Store disambiguation results from API response
          disambiguationResults: {
            entities: data.entities || [],
            topCandidate: data.topCandidate || null,
            needsDisambiguation: data.needsDisambiguation || false,
            autoSelected: data.autoSelected || false,
            confidence: data.confidence || 0,
            method: data.method || 'unknown',
            analytics: data.analytics || {}
          },
          // Store disambiguation metadata for audit trails
          lastDisambiguation: {
            timestamp: new Date().toISOString(),
            inputIdentifiers: disambiguationPayload.identifiers,
            responseMetadata: data.analytics
          }
        }
      }
      
      return data
    } catch (err) {
      error.value = err.response?.data?.message || 'Failed to disambiguate entity'
      console.error('Disambiguation error:', {
        status: err.response?.status,
        data: err.response?.data,
        payload
      })
      throw err
    } finally {
      loading.value = false
    }
  }

  // NEW: Enhanced disambiguation with company-specific structured data
  async function disambiguateCompany(projectId, sessionId, requestPayload) {
    // Pass the complete payload directly as it already matches API spec
    return await disambiguateEntity(projectId, sessionId, requestPayload)
  }

  // NEW: Enhanced disambiguation with person-specific structured data
  async function disambiguatePerson(projectId, sessionId, requestPayload) {
    // Pass the complete payload directly as it already matches API spec
    return await disambiguateEntity(projectId, sessionId, requestPayload)
  }

  // Get session data statistics before deletion
  async function getSessionDataStats(projectId, sessionId) {
    if (!projectId || !sessionId) throw new Error('Missing projectId or sessionId')
    loading.value = true
    error.value = null
    
    try {
      const response = await api.get(`/sessions/${projectId}/sessions/${sessionId}/data-stats`)
      return response.data.stats
    } catch (err) {
      // Gracefully handle when backend doesn't support data stats yet
      if (err.response?.status === 404) {
        console.warn('Data stats endpoint not implemented yet')
        return null
      }
      error.value = err.response?.data?.message || 'Failed to fetch session data stats'
      throw err
    } finally {
      loading.value = false
    }
  }

  // Enhanced session deletion with cascade options
  async function deleteSession(projectId, sessionId, options = {}) {
    if (!projectId || !sessionId) throw new Error('Missing projectId or sessionId')
    loading.value = true
    error.value = null
    
    try {
      const { deleteType = 'soft', cascadeData = false, reason } = options
      const payload = { deleteType, cascadeData }
      if (reason) payload.reason = reason
      
      const response = await api.delete(`/sessions/${projectId}/sessions/${sessionId}`, { 
        data: payload 
      })
      
      // Update sessions list based on deletion type and response
      if (deleteType === 'hard') {
        // Hard delete - remove from sessions list completely
        sessions.value = sessions.value.filter(s => s._id !== sessionId)
      } else {
        // Soft delete - update session state but keep in list for restoration
        const sessionIndex = sessions.value.findIndex(s => s._id === sessionId)
        if (sessionIndex !== -1) {
          sessions.value[sessionIndex] = {
            ...sessions.value[sessionIndex],
            sessionState: 'deleted',
            deleted: true,
            deletedAt: response.data.session?.deletedAt || new Date().toISOString(),
            restorationDeadline: response.data.session?.restorationDeadline
          }
        }
      }
      
      // Clear current session if it's the one being deleted
      if (session.value && session.value._id === sessionId) {
        clearSession()
      }
      
      return response.data
    } catch (err) {
      // For enhanced deletion, if backend doesn't support it yet, fall back to simple deletion
      if (err.response?.status === 400 || err.response?.status === 404) {
        console.warn('Enhanced deletion not supported, attempting simple delete fallback')
        try {
          const response = await api.delete(`/sessions/${projectId}/sessions/${sessionId}`)
          sessions.value = sessions.value.filter(s => s._id !== sessionId)
          if (session.value && session.value._id === sessionId) {
            clearSession()
          }
          return response.data || { success: true, message: 'Session deleted successfully' }
        } catch (fallbackErr) {
          error.value = fallbackErr.response?.data?.message || 'Failed to delete session'
          throw fallbackErr
        }
      } else {
        error.value = err.response?.data?.message || 'Failed to delete session'
        throw err
      }
    } finally {
      loading.value = false
    }
  }

  // Get deleted sessions summary
  async function getDeletedSessionsSummary(projectId) {
    if (!projectId) throw new Error('No projectId provided')
    loading.value = true
    error.value = null
    
    try {
      const response = await api.get(`/sessions/${projectId}/sessions/deleted/summary`)
      return response.data.summary
    } catch (err) {
      if (err.response?.status === 404) {
        console.warn('Deleted sessions summary endpoint not implemented yet')
        // Calculate summary from deleted sessions list as fallback
        try {
          const deletedSessions = await fetchDeletedSessions(projectId)
          const now = new Date()
          const urgentSessions = deletedSessions.filter(s => {
            const deletedAt = new Date(s.deletedAt)
            const daysSince = Math.floor((now - deletedAt) / (1000 * 60 * 60 * 24))
            return daysSince > 27 // Urgent if > 27 days (3 days until permanent)
          })
          
          return {
            restorableCount: deletedSessions.filter(s => s.canRestore !== false).length,
            expiredCount: deletedSessions.filter(s => s.canRestore === false).length,
            urgentCount: urgentSessions.length,
            totalDeleted: deletedSessions.length,
            urgentSessions: urgentSessions.slice(0, 5) // Top 5 urgent sessions
          }
        } catch (fallbackErr) {
          return null
        }
      }
      error.value = err.response?.data?.message || 'Failed to get deleted sessions summary'
      throw err
    } finally {
      loading.value = false
    }
  }

  // Restore a soft-deleted session
  async function restoreSession(projectId, sessionId) {
    if (!projectId || !sessionId) throw new Error('Missing projectId or sessionId')
    loading.value = true
    error.value = null
    
    try {
      const response = await api.post(`/sessions/${projectId}/sessions/${sessionId}/restore`)
      
      // Handle the updated API response structure
      const restoredSession = response.data.session
      
      // Update session in list - preserve both status and sessionState fields correctly
      const sessionIndex = sessions.value.findIndex(s => s._id === sessionId)
      if (sessionIndex !== -1) {
        sessions.value[sessionIndex] = {
          ...restoredSession,
          // sessionState represents lifecycle (active/archived/deleted)
          sessionState: restoredSession.sessionState || 'active',
          // status represents workflow progress (searching/completed/in_progress)
          status: restoredSession.status || sessions.value[sessionIndex].status,
          deleted: restoredSession.deleted || false
        }
      }
      
      return response.data
    } catch (err) {
      // Gracefully handle when backend doesn't support session restoration yet
      if (err.response?.status === 404 || err.response?.status === 500) {
        console.warn('Session restoration endpoint not implemented yet')
        error.value = 'Session restoration is not available yet'
      } else {
        error.value = err.response?.data?.message || 'Failed to restore session'
      }
      throw err
    } finally {
      loading.value = false
    }
  }

  // Get deleted sessions for restoration
  async function fetchDeletedSessions(projectId, options = {}) {
    if (!projectId) throw new Error('No projectId provided')
    loading.value = true
    error.value = null
    
    try {
      // Build query parameters based on API spec
      const params = new URLSearchParams()
      if (options.includeExpired !== undefined) params.append('includeExpired', options.includeExpired)
      if (options.sortBy) params.append('sortBy', options.sortBy)
      if (options.sortOrder) params.append('sortOrder', options.sortOrder)
      if (options.limit) params.append('limit', options.limit)
      if (options.page) params.append('page', options.page)
      
      const queryString = params.toString()
      const url = `/sessions/${projectId}/sessions/deleted/list${queryString ? '?' + queryString : ''}`
      
      const response = await api.get(url)
      
      // Handle different possible response structures
      if (response.data.deletedSessions) {
        return response.data.deletedSessions
      } else if (response.data.sessions) {
        // Some implementations might return sessions array directly
        return response.data.sessions.filter(s => s.deleted || s.status === 'deleted')
      } else {
        return []
      }
    } catch (err) {
      // If the /deleted endpoint doesn't exist, try the status filter approach
      if (err.response?.status === 404) {
        console.warn('Dedicated /sessions/deleted endpoint not found, trying status filter')
        try {
          const params = new URLSearchParams({ status: 'deleted' })
          if (options.limit) params.append('limit', options.limit)
          if (options.page) params.append('page', options.page)
          
          const response = await api.get(`/sessions/${projectId}/sessions?${params.toString()}`)
          return response.data.sessions?.filter(s => s.deleted || s.status === 'deleted') || []
        } catch (statusErr) {
          console.warn('Status filter approach failed, trying deleted=true filter')
          try {
            const response = await api.get(`/sessions/${projectId}/sessions?deleted=true`)
            return response.data.sessions?.filter(s => s.deleted) || []
          } catch (deletedErr) {
            console.warn('All deleted session fetch approaches failed, returning empty array')
            return []
          }
        }
      } else if (err.response?.status === 500) {
        console.warn('Server error fetching deleted sessions, returning empty array')
        return []
      }
      
      error.value = err.response?.data?.message || 'Failed to fetch deleted sessions'
      console.error('Failed to fetch deleted sessions:', err)
      return [] // Return empty array instead of throwing to prevent UI breaks
    } finally {
      loading.value = false
    }
  }

  // Get disambiguation status
  async function getDisambiguationStatus(projectId, sessionId) {
    if (!projectId || !sessionId) throw new Error('Missing projectId or sessionId')
    loading.value = true
    error.value = null
    
    try {
      const response = await api.get(`/sessions/${projectId}/sessions/${sessionId}/disambiguation/status`)
      return response.data
    } catch (err) {
      // Gracefully handle when backend doesn't support status endpoint yet
      if (err.response?.status === 404 || err.response?.status === 500) {
        console.warn('Disambiguation status endpoint not implemented yet')
        return { status: 'unknown', progress: { currentStep: 'disambiguation', totalSteps: 4 } }
      }
      error.value = err.response?.data?.message || 'Failed to get disambiguation status'
      throw err
    } finally {
      loading.value = false
    }
  }

  // Get disambiguation results/candidates
  async function getDisambiguationResults(projectId, sessionId) {
    if (!projectId || !sessionId) throw new Error('Missing projectId or sessionId')
    loading.value = true
    error.value = null
    
    try {
      const response = await api.get(`/sessions/${projectId}/sessions/${sessionId}/disambiguation/results`)
      return response.data
    } catch (err) {
      // Gracefully handle when backend doesn't support results endpoint yet
      if (err.response?.status === 404 || err.response?.status === 500) {
        console.warn('Disambiguation results endpoint not implemented yet')
        return { candidates: [], totalCandidates: 0, processingComplete: false }
      }
      error.value = err.response?.data?.message || 'Failed to get disambiguation results'
      throw err
    } finally {
      loading.value = false
    }
  }

  // Select entity from disambiguation candidates
  async function selectEntity(projectId, sessionId, selectedCandidates) {
    if (!projectId || !sessionId) throw new Error('Missing projectId or sessionId')
    loading.value = true
    error.value = null
    
    try {
      const response = await api.post(`/sessions/${projectId}/sessions/${sessionId}/select-entity`, {
        selectedCandidates
      })
      
      // Update current session if it matches
      if (session.value && session.value._id === sessionId) {
        session.value = { 
          ...session.value, 
          selectedEntities: response.data.selectedEntities,
          phase: response.data.nextPhase || 'data_collection'
        }
      }
      
      return response.data
    } catch (err) {
      error.value = err.response?.data?.message || 'Failed to select entity'
      throw err
    } finally {
      loading.value = false
    }
  }

  // Create manual entity when disambiguation doesn't return satisfactory results
  async function createManualEntity(projectId, sessionId, entityData) {
    if (!projectId || !sessionId) throw new Error('Missing projectId or sessionId')
    loading.value = true
    error.value = null
    
    try {
      const payload = {
        name: entityData.name,
        entityType: entityData.entityType,
        confidence: 0.5, // Default medium confidence for manual entries
        description: entityData.description,
        additionalInfo: entityData.additionalInfo || {}
      }
      
      console.log('Creating manual entity:', payload)
      
      const response = await api.post(
        `/sessions/${projectId}/sessions/${sessionId}/create-manual-entity`,
        payload
      )
      
      console.log('Manual entity created:', response.data)
      
      // Update current session with the created entity
      if (session.value && session.value._id === sessionId) {
        session.value = {
          ...session.value,
          // Add the manual entity to disambiguation results
          disambiguationResults: {
            ...(session.value.disambiguationResults || {}),
            entities: [
              response.data.entity,
              ...(session.value.disambiguationResults?.entities || [])
            ],
            manualEntityCreated: true
          },
          // Update phase if returned
          phase: response.data.nextPhase || session.value.phase
        }
      }
      
      return response.data.entity
    } catch (err) {
      error.value = err.response?.data?.message || 'Failed to create manual entity'
      console.error('Manual entity creation error:', {
        status: err.response?.status,
        data: err.response?.data,
        payload: entityData
      })
      throw err
    } finally {
      loading.value = false
    }
  }

  function clearSession() {
    session.value = null
    localStorage.removeItem('diligint-session-id')
    localStorage.removeItem('diligint-project-id')
  }

  // ENHANCED: Computed properties for structured disambiguation results
  const disambiguationResult = computed(() => 
    session.value?.disambiguationResults || null
  )

  const entities = computed(() => 
    disambiguationResult.value?.entities || 
    disambiguationResult.value?.candidates || 
    []
  )

  const topEntity = computed(() => 
    disambiguationResult.value?.topCandidate || 
    entities.value[0] || 
    null
  )

  // NEW: Enhanced entity properties for structured data
  const entityName = computed(() => 
    topEntity.value?.name ||
    session.value?.questionsGenerated?.entityName || 
    session.value?.searchQuery || 
    ''
  )
  
  const entityType = computed(() => 
    topEntity.value?.entityType ||
    session.value?.questionsGenerated?.entityType || 
    session.value?.entityType || 
    ''
  )
  
  // NEW: Reference-based identifiers for document separation compliance
  const entityIdentifiers = computed(() => ({
    aviatoId: topEntity.value?.metadata?.aviatoId || null,
    primaryId: topEntity.value?.id || null,
    structuredIds: topEntity.value?.context?.identification || {},
    externalRefs: topEntity.value?.metadata || {}
  }))
  
  const enrichedContext = computed(() => 
    topEntity.value?.context || {}
  )
  
  const combinedSources = computed(() => 
    ({ source: topEntity.value?.source || 'unknown' })
  )
  
  const primarySources = computed(() => 
    topEntity.value?.context?.urls || []
  )
  
  const secondarySources = computed(() => 
    topEntity.value?.context?.socialProfiles ? Object.values(topEntity.value.context.socialProfiles) : []
  )
  
  const allSources = computed(() => [
    ...(topEntity.value?.context?.urls || []),
    ...(topEntity.value?.context?.socialProfiles ? Object.values(topEntity.value.context.socialProfiles) : [])
  ])
  
  const alternativeSources = computed(() => 
    entities.value.filter(e => e.id !== topEntity.value?.id) || []
  )

  // NEW: Enhanced quality and confidence metrics
  const dataQuality = computed(() => {
    const analytics = disambiguationResult.value?.analytics || {}
    const metadata = topEntity.value?.metadata || {}
    return {
      overall: metadata.dataQuality === 'high' ? 0.9 : metadata.dataQuality === 'medium' ? 0.7 : 0.5,
      identifierVerification: 0.8, // Default based on API response
      sourceVerification: analytics.sourcesUsed > 1 ? 0.9 : 0.7,
      contextCompleteness: Object.keys(enrichedContext.value).length > 3 ? 0.8 : 0.6,
      structuredDataScore: Object.keys(entityIdentifiers.value.structuredIds).length > 0 ? 0.9 : 0.5
    }
  })

  const disambiguationConfidence = computed(() => 
    disambiguationResult.value?.confidence || 
    topEntity.value?.totalScore || 
    0
  )

  const disambiguationMetadata = computed(() => 
    disambiguationResult.value?.analytics || {}
  )

  // NEW: Audit trail information for due diligence
  const auditTrail = computed(() => ({
    inputIdentifiers: session.value?.lastDisambiguation?.inputIdentifiers || {},
    method: disambiguationResult.value?.method || 'unknown',
    searchDuration: disambiguationMetadata.value?.searchDuration || 0,
    sourcesUsed: disambiguationMetadata.value?.sourcesUsed || 0,
    candidatesFound: disambiguationMetadata.value?.candidatesFound || 0,
    processingTimestamp: session.value?.lastDisambiguation?.timestamp,
    autoSelected: disambiguationResult.value?.autoSelected || false
  }))

  // NEW: Structured data validation status
  const identifierValidation = computed(() => {
    const inputIds = session.value?.lastDisambiguation?.inputIdentifiers || {}
    const entityIds = entityIdentifiers.value.structuredIds || {}
    return {
      valid: Object.keys(entityIds),
      invalid: [],
      warnings: [],
      verified: Object.keys(inputIds).filter(key => entityIds[key])
    }
  })
  
  const hasRichContext = computed(() => {
    const context = enrichedContext.value
    return context.title || context.location || context.urls?.length > 0 || context.socialProfiles
  })
  
  const hasVerifiedSources = computed(() => 
    topEntity.value?.source === 'aviato' || primarySources.value.length > 0
  )
  
  const needsDisambiguation = computed(() => 
    disambiguationResult.value?.needsDisambiguation || 
    entities.value.length > 1 ||
    disambiguationConfidence.value < 0.8
  )

  // NEW: Enhanced quality indicators
  const hasStructuredData = computed(() => 
    Object.keys(entityIdentifiers.value.structuredIds || {}).length > 0 || 
    topEntity.value?.context?.identification
  )

  const isHighConfidence = computed(() => 
    disambiguationConfidence.value >= 0.9 && 
    dataQuality.value.overall >= 0.8
  )

  const requiresManualReview = computed(() => 
    disambiguationConfidence.value < 0.7 || 
    identifierValidation.value.invalid.length > 0 ||
    auditTrail.value.fallbackUsed
  )

  // Legacy computed properties for backward compatibility
  const aviatoId = computed(() => 
    entityIdentifiers.value.aviatoId
  )

  const candidates = computed(() => 
    entities.value
  )

  const selectedCandidates = computed(() => {
    if (session.value?.selectedEntity?.candidateId) {
      return [session.value.selectedEntity.candidateId]
    }
    return []
  })

    return {
    // State
    session,
    sessions,
    sessionMetadata,        // Enhanced metadata with phase breakdowns
    loading,
    error,
    
    // Core Session Management
    fetchSessions,          // Enhanced with filtering and pagination options
    loadSession,
    createSession,
    updateSession,
    clearSession,
    
    // Session Progress & Navigation (NEW)
    getSessionProgress,     // Get comprehensive progress information
    navigateToPhase,        // Navigate to specific phase with validation
    updateSessionPhase,     // Legacy phase update (for backward compatibility)
    
    // Session Organization
    toggleBookmark,         // Bookmark management
    manageTags,            // Tag management
    
    // Session Lifecycle Management
    deleteSession,          // Enhanced deletion with soft/hard options
    getSessionDataStats,    // Data statistics before deletion
    restoreSession,         // Session restoration
    fetchDeletedSessions,   // Deleted sessions list
    getDeletedSessionsSummary, // NEW: Deleted sessions summary
    
    // Entity Disambiguation Workflow
    disambiguateEntity,
    getDisambiguationStatus, // Status checking
    getDisambiguationResults, // Results fetching
    selectEntity,           // Entity selection
    createManualEntity,     // Manual entity creation
    disambiguateCompany,    // Company-specific disambiguation
    disambiguatePerson,     // Person-specific disambiguation
    
    // Enhanced computed properties for structured disambiguation
    disambiguationResult,
    entities,
    topEntity,
    entityName,
    entityType,
    entityIdentifiers,      // Reference-based identifiers
    enrichedContext,
    combinedSources,
    primarySources,
    secondarySources,
    allSources,
    alternativeSources,
    
    // Quality and audit properties
    dataQuality,
    disambiguationConfidence,
    disambiguationMetadata,
    auditTrail,             // Due diligence audit trail
    identifierValidation,   // Structured data validation
    
    // Quality indicators
    hasRichContext,
    hasVerifiedSources,
    hasStructuredData,
    isHighConfidence,
    requiresManualReview,
    needsDisambiguation,
    
    // Legacy computed properties (backward compatibility)
    aviatoId,
    candidates,
    selectedCandidates
  }
})